import { Currency } from "@redotech/money/currencies";
import { createEnum } from "@redotech/zod/zod-enum";
import * as z from "zod/v4";

export const zCurrency = createEnum(Currency, { enumName: "Currency" });

export const zUsdDollars = z.number().brand<"dollars">();
export type UsdDollars = z.infer<typeof zUsdDollars>;

export const zUsdCents = z.number().brand<"cents">();
export type UsdCents = z.infer<typeof zUsdCents>;

export const zUsdThousandthCents = z.number().brand<"thousandthCents">();
export type UsdThousandthCents = z.infer<typeof zUsdThousandthCents>;

export const zUsdMicros = z.number().brand<"micros">();
export type UsdMicros = z.infer<typeof zUsdMicros>;

type CurrencyAmount = UsdDollars | UsdCents | UsdThousandthCents | UsdMicros;

const DOLLAR_TO_MICROS = 1_000_000;
const CENT_TO_THOUSANDTH_CENTS = 1000;
const DOLLAR_TO_CENTS = 100;

/**
 * Converts dollars to cents with precise integer arithmetic to avoid floating point precision issues.
 * @param dollars - Dollar amount as a number
 * @returns Integer cents value
 */
export function dollarsToCents(dollars: UsdDollars): UsdCents {
  return Math.round(dollars * DOLLAR_TO_CENTS) as UsdCents;
}

/**
 * Converts dollars to thousandth-cents with precise integer arithmetic to avoid floating point precision issues.
 * @param dollars - Dollar amount as a number
 * @returns Integer thousandth-cents value
 */
export function dollarsToThousandthCents(
  dollars: UsdDollars,
): UsdThousandthCents {
  return Math.round(
    dollars * DOLLAR_TO_CENTS * CENT_TO_THOUSANDTH_CENTS,
  ) as UsdThousandthCents;
}

/**
 * Converts cents to dollars with precise arithmetic to avoid floating point precision issues.
 * @param cents - Cents amount as a number
 * @returns Dollar amount as a number
 */
export function centsToDollars(cents: UsdCents): UsdDollars {
  return (Math.round(cents) / DOLLAR_TO_CENTS) as UsdDollars;
}

/**
 * Converts thousandth-cents to dollars with precise arithmetic to avoid floating point precision issues.
 * @param thousandthCents - Thousandth-cents amount as a number
 * @returns Dollar amount as a number
 */
export function thousandthCentsToDollars(
  thousandthCents: UsdThousandthCents,
): UsdDollars {
  return (Math.round(thousandthCents) /
    (DOLLAR_TO_CENTS * CENT_TO_THOUSANDTH_CENTS)) as UsdDollars;
}

/**
 * Converts micros to dollars with precise arithmetic to avoid floating point precision issues.
 * @param micros - Micros amount as a number
 * @returns Dollar amount as a number
 */
export function microsToDollars(micros: UsdMicros): UsdDollars {
  return (Math.round(micros) / DOLLAR_TO_MICROS) as UsdDollars;
}

/**
 * Converts dollars to micros with precise integer arithmetic to avoid floating point precision issues.
 * @param dollars - Dollar amount as a number
 * @returns Integer micros value
 */
export function dollarsToMicros(dollars: UsdDollars): UsdMicros {
  return Math.round(dollars * DOLLAR_TO_MICROS) as UsdMicros;
}

export function thousandthCentsToMicros(
  thousandthCents: UsdThousandthCents,
): UsdMicros {
  return (Math.round(thousandthCents) * 10) as UsdMicros;
}

export function addMoney<T extends CurrencyAmount>(...amounts: T[]): T {
  return (amounts as number[]).reduce((acc, amount) => acc + amount, 0) as T;
}

export function multiplyMoney<T extends CurrencyAmount>(
  amount: T,
  factor: number,
): T {
  return (amount * factor) as T;
}

/**
 * Rounds a currency amount to a given number of decimal places.
 * @param amount - The currency amount to round.
 * @param decimals - The number of decimal places to round to.
 * @returns The rounded currency amount.
 */
export function roundMoney<T extends CurrencyAmount>({
  amount,
  decimals,
}: {
  amount: T;
  decimals: number;
}): T {
  return Number(amount.toFixed(decimals)) as T;
}

/**
 * Rounds a currency amount down to a given number of decimal places.
 * @param amount - The currency amount to round down.
 * @param decimals - The number of decimal places to round to.
 * @returns The rounded down currency amount.
 */
export function floorMoney<T extends CurrencyAmount>({
  amount,
  decimals = 0,
}: {
  amount: T;
  decimals?: number;
}): T {
  return (Math.floor(amount * 10 ** decimals) / 10 ** decimals) as T;
}
